import React, { useState, useEffect, useRef } from 'react';
import { X, User, Phone, Mail, MessageSquare, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { RichTextEditor } from '../intake/RichTextEditor';

interface PatientLead {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  email: string | null;
  comments: string | null;
  mapped_patient_id: string | null;
  status: string;
  created_at: string;
  created_by: string;
}

interface LeadStatus {
  id: string;
  name: string;
  slug: string;
  color: string;
  icon: string;
  display_order: number;
  is_default: boolean;
}

interface PatientLeadFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (lead: Partial<PatientLead>) => Promise<void>;
  lead: Partial<PatientLead> | null;
  isSubmitting: boolean;
}

export function PatientLeadForm({ 
  isOpen, 
  onClose, 
  onSave, 
  lead, 
  isSubmitting 
}: PatientLeadFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [comments, setComments] = useState('');
  const [status, setStatus] = useState('');
  const [statuses, setStatuses] = useState<LeadStatus[]>([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchStatuses();
      if (firstNameInputRef.current) {
        firstNameInputRef.current.focus();
      }
      
      // Small delay to allow the modal to render before animating
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);
      
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (lead) {
      setFirstName(lead.first_name || '');
      setLastName(lead.last_name || '');
      setPhoneNumber(lead.phone_number || '');
      setEmail(lead.email || '');
      setComments(lead.comments || '');
      setStatus(lead.status || '');
    } else {
      setFirstName('');
      setLastName('');
      setPhoneNumber('');
      setEmail('');
      setComments('');
      // Set to default status when creating a new lead
      const defaultStatus = statuses.find(s => s.is_default);
      setStatus(defaultStatus?.slug || 'new');
    }
  }, [lead, statuses]);

  const fetchStatuses = async () => {
    try {
      setLoadingStatuses(true);
      const { data, error } = await supabase.rpc('get_lead_statuses');
      
      if (error) throw error;
      
      setStatuses(data || []);
      
      // If creating a new lead, set to default status
      if (!lead?.id) {
        const defaultStatus = data.find((s: LeadStatus) => s.is_default);
        if (defaultStatus) {
          setStatus(defaultStatus.slug);
        }
      }
    } catch (error) {
      console.error('Error fetching lead statuses:', error);
    } finally {
      setLoadingStatuses(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const updatedLead = {
      ...lead,
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber || null,
      email: email || null,
      comments: comments || null,
      status
    };
    
    await onSave(updatedLead);
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedNumber = formatPhoneNumber(e.target.value);
    setPhoneNumber(formattedNumber);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-0 flex items-center justify-center p-4 z-50 transition-all duration-300 ease-in-out"
      style={{ 
        backgroundColor: isVisible ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0)',
        backdropFilter: isVisible ? 'blur(2px)' : 'blur(0px)'
      }}
    >
      <div 
        ref={modalRef}
        className={`bg-white rounded-lg shadow-xl max-w-lg w-full transform transition-all duration-300 ease-out ${
          isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-8'
        }`}
      >
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-green-50 to-blue-50">
          <h3 className="text-xl font-semibold text-gray-900">
            {lead?.id ? 'Edit Patient Lead' : 'Add New Patient Lead'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="block text-base font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    ref={firstNameInputRef}
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-base"
                    placeholder="First name"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-base font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-base"
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-base font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-base"
                  placeholder="(555) 123-4567"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Either phone number or email is required
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-base font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-base"
                  placeholder="email@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="status" className="block text-base font-medium text-gray-700 mb-2">
                Status
              </label>
              {loadingStatuses ? (
                <div className="mt-1 flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500 mr-2"></div>
                  <span className="text-sm text-gray-500">Loading statuses...</span>
                </div>
              ) : (
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-base"
                >
                  {statuses.map((statusOption) => (
                    <option key={statusOption.id} value={statusOption.slug}>
                      {statusOption.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label htmlFor="comments" className="block text-base font-medium text-gray-700 mb-2">
                Comments
              </label>
              <RichTextEditor
                content={comments}
                onChange={setComments}
                placeholder="Additional notes or comments"
                className="min-h-[150px]"
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-5 py-2.5 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors"
              disabled={isSubmitting || (!firstName.trim() || !lastName.trim() || (!phoneNumber.trim() && !email.trim()))}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Save
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}